import React, { useEffect, useMemo, useState } from "react";
import DataTable from "../components/common/DataTable";
import ConfirmModal from "../components/common/ConfirmModal";
import BlockNoteEditor from "../components/common/BlockNoteEditor";
import { useAdminApi } from "../hooks/useAdminApi";
import { NewsPost, Paginated } from "../types/admin";
import { toast } from "react-toastify";

const emptyPost: Partial<NewsPost> = {
	title: "",
	body_markdown: "",
	category: "news",
	status: "draft",
	pinned: false,
	locale: "ja-JP",
};

const NewsPage: React.FC = () => {
	const api = useAdminApi();
	const [posts, setPosts] = useState<Paginated<NewsPost> | null>(null);
	const [loading, setLoading] = useState(false);
	const [detailLoading, setDetailLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [filters, setFilters] = useState({ status: "", category: "" });
	const [editing, setEditing] = useState<Partial<NewsPost>>(emptyPost);
	const [modal, setModal] = useState<{ open: boolean; id?: number }>({
		open: false,
	});

	const fetchNews = async () => {
		setLoading(true);
		setError(null);
		try {
			const res = await api.listNews({
				status: filters.status || undefined,
				category: filters.category || undefined,
			});
			setPosts(res);
		} catch (err) {
			console.error(err);
			setError("ニュースの取得に失敗しました");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchNews();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const columns = useMemo(
		() => [
			{ key: "id", header: "ID" },
			{ key: "title", header: "タイトル" },
			{ key: "category", header: "カテゴリ" },
			{ key: "status", header: "状態" },
			{ key: "published_at", header: "公開" },
		],
		[]
	);

	const startEdit = async (post?: NewsPost) => {
		if (!post?.id) {
			setEditing({ ...emptyPost });
			return;
		}

		setDetailLoading(true);
		try {
			const full = await api.getNews(post.id);
			setEditing(full);
		} catch (err) {
			console.error(err);
			toast.error("記事詳細の取得に失敗しました");
			setEditing({ ...post });
		} finally {
			setDetailLoading(false);
		}
	};

	const save = async () => {
		if (!editing.title || !editing.body_markdown || !editing.category) {
			toast.error("タイトル・本文・カテゴリは必須です");
			return;
		}
		try {
			if (editing.id) {
				await api.updateNews(editing.id, editing);
				toast.success("更新しました");
			} else {
				const res = await api.createNews(editing);
				setEditing(res.data?.data ?? res.data);
				toast.success("作成しました");
			}
			fetchNews();
		} catch (err) {
			console.error(err);
			toast.error("保存に失敗しました");
		}
	};

	const publish = async () => {
		if (!editing.id) return;
		try {
			await api.publishNews(editing.id);
			toast.success("公開しました");
			fetchNews();
		} catch (err) {
			console.error(err);
			toast.error("公開に失敗しました");
		}
	};

	const togglePin = async () => {
		if (!editing.id) return;
		try {
			await api.pinNews(editing.id, !editing.pinned);
			setEditing((prev) => (prev ? { ...prev, pinned: !prev.pinned } : prev));
			toast.success("ピンを更新しました");
			fetchNews();
		} catch (err) {
			console.error(err);
			toast.error("ピン更新に失敗しました");
		}
	};

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-xl font-semibold text-ink">News</h1>
					<p className="text-sm text-muted">
						リリースノート/メンテ情報を作成・公開
					</p>
				</div>
				<button
					className="rounded-xl bg-primary px-3 sm:px-4 py-2 text-sm font-semibold text-white"
					onClick={() => startEdit()}
				>
					新規作成
				</button>
			</div>

			<div className="rounded-2xl border border-surface-border bg-white p-4 shadow-card">
				<div className="grid gap-2 md:grid-cols-3">
					<select
						className="input"
						value={filters.status}
						onChange={(e) => setFilters({ ...filters, status: e.target.value })}
					>
						<option value="">状態</option>
						<option value="draft">draft</option>
						<option value="published">published</option>
					</select>
					<select
						className="input"
						value={filters.category}
						onChange={(e) =>
							setFilters({ ...filters, category: e.target.value })
						}
					>
						<option value="">カテゴリ</option>
						<option value="news">news</option>
						<option value="release">release</option>
						<option value="maintenance">maintenance</option>
					</select>
					<button
						className="rounded-xl bg-primary px-3 sm:px-4 py-2 text-sm font-semibold text-white"
						onClick={fetchNews}
					>
						絞り込み
					</button>
				</div>

				<div className="mt-4">
					<DataTable
						data={posts?.data ?? []}
						columns={columns}
						total={posts?.total ?? 0}
						loading={loading}
						error={error}
						onRowClick={(row) => startEdit(row as NewsPost)}
					/>
				</div>
			</div>

			{editing && (
				<div className="rounded-2xl border border-surface-border bg-white p-4 shadow-card space-y-3">
					{detailLoading && (
						<p className="text-sm text-muted">本文を読み込み中…</p>
					)}
					<div className="grid gap-3 md:grid-cols-2">
						<label className="form-field">
							タイトル
							<input
								className="input"
								value={editing.title}
								onChange={(e) =>
									setEditing({ ...editing, title: e.target.value })
								}
							/>
						</label>
						<label className="form-field">
							カテゴリ
							<select
								className="input"
								value={editing.category}
								onChange={(e) =>
									setEditing({ ...editing, category: e.target.value as any })
								}
							>
								<option value="news">news</option>
								<option value="release">release</option>
								<option value="maintenance">maintenance</option>
							</select>
						</label>
						<label className="form-field">
							ロケール
							<input
								className="input"
								value={editing.locale ?? ""}
								onChange={(e) =>
									setEditing({ ...editing, locale: e.target.value })
								}
								placeholder="ja-JP"
							/>
						</label>
						<label className="form-field">
							サービス種別
							<input
								className="input"
								value={editing.service_type ?? ""}
								onChange={(e) =>
									setEditing({ ...editing, service_type: e.target.value })
								}
								placeholder="ticketing"
							/>
						</label>
					</div>

					<div className="flex items-center gap-3 text-sm">
						<span className="text-muted">状態: {editing.status}</span>
						{editing.id && (
							<button className="pill" onClick={togglePin}>
								{editing.pinned ? "Unpin" : "Pin"}
							</button>
						)}
					</div>

					<BlockNoteEditor
						value={editing.body_markdown ?? ""}
						onChange={(val) => setEditing({ ...editing, body_markdown: val })}
						minHeight="360px"
					/>

					<div className="flex gap-2 justify-end">
						<button className="pill" onClick={save}>
							保存
						</button>
						{editing.id && (
							<button
								className="rounded-xl bg-primary px-3 sm:px-4 py-2 text-sm font-semibold text-white"
								onClick={publish}
							>
								公開する
							</button>
						)}
					</div>
				</div>
			)}

			<ConfirmModal
				open={modal.open}
				title=""
				message=""
				onCancel={() => setModal({ open: false })}
				onConfirm={() => setModal({ open: false })}
				showReason={false}
				requireReason={false}
			/>
		</div>
	);
};

export default NewsPage;
